"use client";

import { useT } from "../../components/utilities/TranslatedText";

import { ReactNode, useState } from "react";
import {
  Avatar,
  Flex,
  HoverCard,
  ScrollArea,
  Separator,
  Skeleton,
  Text,
} from "@radix-ui/themes";
import { unescapeHtml } from "../../utils/StringUtilities";
import {
  getSubBlockIcon,
  IProcessedGroupSubBlock,
} from "../../components/blocks/SchoolBlocks/GroupBlock/GroupBlock";
import { usePageBlockPreview } from "./usePageBlockPreview";
import styles from "./PageIndexPage.module.scss";
import { GridTypes } from "../../utils/SchoolBlocksUtilities";

function BlockRow({ block }: { block: IProcessedGroupSubBlock }) {
  return (
    <Flex align="center" gap="3">
      <Avatar
        size="4"
        radius="small"
        color="gray"
        src={block.picture || undefined}
        alt={block.title}
        fallback={getSubBlockIcon(block) ?? ""}
      />
      <Text size="2" weight="medium">
        {unescapeHtml(block.title)}
      </Text>
    </Flex>
  );
}

function BlockRowSkeleton({ textLength }: { textLength: number }) {
  return (
    <Flex align="center" gap="3">
      <Skeleton>
        <Avatar size="4" radius="small" fallback="" />
      </Skeleton>
      <Skeleton>
        <Text size="2">{" ".repeat(textLength)}</Text>
      </Skeleton>
    </Flex>
  );
}

type PageBlockPreviewProps = {
  organizationId: string | null | undefined;
  children: ReactNode;
};

function PageBlockPreview({ organizationId, children }: PageBlockPreviewProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const { data, isLoading } = usePageBlockPreview(
    open && organizationId ? organizationId : null,
  );

  if (!organizationId) {
    return <>{children}</>;
  }

  return (
    <HoverCard.Root openDelay={150} onOpenChange={setOpen}>
      <HoverCard.Trigger>
        <span className={styles.hoverPreviewTrigger}>{children}</span>
      </HoverCard.Trigger>
      <HoverCard.Content
        side="right"
        align="start"
        width="380px"
        className={styles.hoverPreviewContent}
        onClick={(e) => e.stopPropagation()}
      >
        <ScrollArea
          tabIndex={0}
          role="region"
          aria-label={t("Page preview")}
          scrollbars="vertical"
          style={{ maxHeight: "200px" }}
        >
          <Flex direction="column" gap="3">
            {isLoading ? (
              <>
                <BlockRowSkeleton textLength={22} />
                <BlockRowSkeleton textLength={16} />
                <BlockRowSkeleton textLength={19} />
              </>
            ) : data?.blocks.length === 0 ? (
              <Text size="2" color="gray">
                No content blocks yet.
              </Text>
            ) : (
              data?.blocks.map((block) => (
                <BlockRow key={block.model.id} block={block} />
              ))
            )}
          </Flex>
        </ScrollArea>
        <Separator size="4" my="3" />
        <Text size="1" color="gray">
          {isLoading
            ? "Loading contents…"
            : `${data.blocks.length} ${data.type === GridTypes.terraced ? "terrace" : "block"}${data.blocks.length === 1 ? "" : "s"}`}
        </Text>
      </HoverCard.Content>
    </HoverCard.Root>
  );
}

export default PageBlockPreview;
